import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { ICONS, rub, formatPrice } from "../../lib/format";
import { Plus, Pencil, Trash2 } from "lucide-react";
import ServiceForm from "./ServiceForm";
import { SortableArea, useSortableItem, DragHandle } from "./Sortable";
import { act } from "../../lib/useFetch";

function ServiceRow({ s, onEdit, onDelete }) {
  const Icon = ICONS[s.icon] || ICONS.Sparkles;
  const { setNodeRef, style, handleProps } = useSortableItem(s.id);
  return (
    <tr ref={setNodeRef} style={style} className="border-t border-white/5 bg-[#0E0E16]" data-testid={`service-row-${s.id}`}>
      <td className="py-3 pr-3"><DragHandle handleProps={handleProps} testid={`service-drag-${s.id}`} /></td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-cyan shrink-0" />
          <div>
            <div className="font-medium">{s.title_ru}</div>
            <div className="text-xs text-gray-500">{s.title_en} · {s.title_cn}</div>
          </div>
        </div>
      </td>
      <td className="py-3 pr-3"><span className="chip">{s.category}</span></td>
      <td className="py-3 pr-3 text-gold font-mono whitespace-nowrap">{formatPrice(s, "ru")}</td>
      <td className="py-3 pr-3 text-xs text-gray-400 whitespace-nowrap">
        {s.price_min != null && s.price_max != null ? `${rub(s.price_min)} – ${rub(s.price_max)}` : "—"}
        {s.auto_price_enabled && <div className="text-cyan">AI вкл.</div>}
      </td>
      <td className="py-3 pr-3 text-xs text-gray-400">{s.tiers.length || "—"}</td>
      <td className="py-3 pr-3 text-xs">{s.is_active ? <span className="text-cyan">Виден</span> : <span className="text-gray-500">Скрыт</span>}</td>
      <td className="py-3 whitespace-nowrap">
        <button onClick={() => onEdit(s)} className="btn-outline !py-1.5 !px-3 mr-2" data-testid={`service-edit-${s.id}`}><Pencil size={14} /></button>
        <button onClick={() => onDelete(s)} className="btn-outline !py-1.5 !px-3" data-testid={`service-delete-${s.id}`}><Trash2 size={14} /></button>
      </td>
    </tr>
  );
}

export default function ServicesPanel() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/services");
      setServices(data.services); setCategories(data.categories);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const reorder = async (next) => {
    setServices(next);
    await act(api.post("/admin/services/reorder", { ids: next.map((s) => s.id) }), "Порядок сохранён");
    load();
  };

  const del = async (s) => {
    if (!window.confirm(`Удалить услугу «${s.title_ru}»?`)) return;
    try { await api.delete(`/admin/services/${s.id}`); toast.success("Услуга удалена"); load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="panel-services">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-heading text-2xl font-bold">📦 Услуги ({services.length})</h3>
        <button onClick={() => setEditing({ sort_order: services.length })} className="btn-gold inline-flex items-center gap-2 !py-2" data-testid="service-add-btn">
          <Plus size={14} /> Добавить
        </button>
      </div>
      <div className="overflow-x-auto">
        <SortableArea items={services} onReorder={reorder}>
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-gray-500">
              <th className="pb-2"></th><th className="pb-2">Название</th><th className="pb-2">Категория</th><th className="pb-2">Цена</th>
              <th className="pb-2">Коридор</th><th className="pb-2">Тарифы</th><th className="pb-2">Статус</th><th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => <ServiceRow key={s.id} s={s} onEdit={setEditing} onDelete={del} />)}
          </tbody>
        </table>
        </SortableArea>
      </div>
      {editing && (
        <ServiceForm initial={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
